import React, { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { API_BASE_URL, extractErrorMessage, putRequest } from "../../../../api";
import { logout, setAuthenticatedUser } from "../../../redux/actions/authActions";
import logo from "../../../images/logo1_aveiro.png";
import "./login.css";
import "./ResetPasswordPage.css";
import "./ChangeInitialPasswordPage.css";

const LockIcon = () => (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="#aab4cb">
        <path d="M18 8H17V6C17 3.24 14.76 1 12 1C9.24 1 7 3.24 7 6V8H6C4.9 8 4 8.9 4 10V20C4 21.1 4.9 22 6 22H18C19.1 22 20 21.1 20 20V10C20 8.9 19.1 8 18 8ZM12 17C10.9 17 10 16.1 10 15C10 13.9 10.9 13 12 13C13.1 13 14 13.9 14 15C14 16.1 13.1 17 12 17ZM15.1 8H8.9V6C8.9 4.29 10.29 2.9 12 2.9C13.71 2.9 15.1 4.29 15.1 6V8Z"/>
    </svg>
);

const EyeIcon = ({ open }) => open ? (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
        <path d="M12 4.5C7 4.5 2.73 7.61 1 12C2.73 16.39 7 19.5 12 19.5C17 19.5 21.27 16.39 23 12C21.27 7.61 17 4.5 12 4.5ZM12 17C9.24 17 7 14.76 7 12C7 9.24 9.24 7 12 7C14.76 7 17 9.24 17 12C17 14.76 14.76 17 12 17Z" fill="#aab4cb"/>
    </svg>
) : (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
        <path d="M12 7C14.76 7 17 9.24 17 12C17 12.65 16.87 13.26 16.64 13.83L19.56 16.75C21.07 15.49 22.26 13.86 22.99 12C21.26 7.61 16.99 4.5 11.99 4.5C10.59 4.5 9.25 4.75 8.01 5.2L10.17 7.36C10.74 7.13 11.35 7 12 7ZM2 4.27L4.28 6.55L4.73 7C3.08 8.3 1.78 10.02 1 12C2.73 16.39 7 19.5 12 19.5C13.55 19.5 15.03 19.2 16.38 18.66L16.8 19.08L19.73 22L21 20.73L3.27 3L2 4.27Z" fill="#aab4cb"/>
    </svg>
);

const ChangeInitialPasswordPage = () => {
    const { i18n } = useTranslation();
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const currentUser = useSelector((state) => state.auth?.user);

    const [form, setForm] = useState({
        currentPassword: "",
        newPassword: "",
        confirmPassword: "",
    });
    const [show, setShow] = useState({
        currentPassword: false,
        newPassword: false,
        confirmPassword: false,
    });
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState("");
    const [success, setSuccess] = useState("");

    useEffect(() => {
        const isRTL = i18n.language === "ar";
        document.documentElement.setAttribute("dir", isRTL ? "rtl" : "ltr");
        document.documentElement.setAttribute("lang", i18n.language);
        document.body.classList.toggle("rtl-layout", isRTL);
    }, [i18n.language]);

    useEffect(() => {
        if (currentUser && currentUser.mustChangePassword === false) {
            navigate("/cards", { replace: true });
        }
    }, [currentUser, navigate]);

    const passwordHints = useMemo(() => {
        const password = form.newPassword || "";
        return {
            length: password.length >= 8,
            upper: /[A-Z]/.test(password),
            lower: /[a-z]/.test(password),
            digit: /[0-9]/.test(password),
            special: /[^A-Za-z0-9]/.test(password),
            different: Boolean(form.currentPassword) && password !== form.currentPassword,
            confirmed: Boolean(form.confirmPassword) && password === form.confirmPassword,
        };
    }, [form.currentPassword, form.newPassword, form.confirmPassword]);

    const formValid = useMemo(() => (
        Boolean(form.currentPassword)
        && Boolean(form.newPassword)
        && Boolean(form.confirmPassword)
        && Object.values(passwordHints).every(Boolean)
    ), [form, passwordHints]);

    const handleChange = (event) => {
        const { name, value } = event.target;
        setForm((prev) => ({ ...prev, [name]: value }));
        setError("");
        setSuccess("");
    };

    const togglePassword = (field) => {
        setShow((prev) => ({ ...prev, [field]: !prev[field] }));
    };

    const handleSubmit = async (event) => {
        event.preventDefault();
        if (!formValid || loading) return;

        setLoading(true);
        setError("");
        setSuccess("");

        try {
            const response = await putRequest({
                url: `${API_BASE_URL}/api/auth/change-initial-password`,
                data: form,
                token: sessionStorage.getItem("token"),
            });

            const updatedUser = response.data?.user || { ...currentUser, mustChangePassword: false };
            dispatch(setAuthenticatedUser(updatedUser));
            localStorage.setItem("user", JSON.stringify(updatedUser));

            setSuccess(response.data?.message || "Votre mot de passe a été modifié avec succès.");

            window.setTimeout(() => {
                navigate("/cards", { replace: true });
            }, 700);
        } catch (apiError) {
            const status = apiError?.response?.status;
            const code = apiError?.response?.data?.code;

            if (status === 401) {
                setError("Le mot de passe actuel est incorrect.");
            } else if (status === 403 && code !== "PASSWORD_CHANGE_REQUIRED") {
                setError("Votre session n'est plus valide. Veuillez vous reconnecter.");
                dispatch(logout());
                navigate("/", { replace: true });
            } else {
                setError(extractErrorMessage(apiError, "Une erreur est survenue lors de la modification du mot de passe."));
            }
        } finally {
            setLoading(false);
        }
    };

    return (
        <>
            <div className="scene">
                <div className="grid-overlay" />
                <div className="wave-container">
                    <div className="wave w1" />
                    <div className="wave w2" />
                    <div className="wave w3" />
                </div>
            </div>

            <div className="login-layout rpp-layout">
                <aside className="brand-panel rpp-brand-panel">
                    <div className="brand-logo">
                        <div className="brand-logo-circle">
                            <img src={logo} alt="Logo Aveiro Maroc" />
                        </div>
                    </div>

                    <h1 className="brand-headline">
                        <span style={{ whiteSpace: "nowrap" }}>Sécurisez votre <span className="accent">compte</span></span>
                        <br />avant de continuer
                    </h1>

                    <p className="brand-desc">
                        Le mot de passe temporaire envoyé lors de l'activation doit être remplacé avant tout accès au reste de l'application.
                    </p>
                </aside>

                <main className="login-panel rpp-login-panel">
                    <div className="login-card rpp-card cip-card">
                        <div className="card-stripe" />
                        <div className="card-body">
                            <div className="rpp-icon-wrap">
                                <div className="rpp-icon-ring">
                                    <LockIcon />
                                </div>
                            </div>

                            <h2>Sécurisez votre compte</h2>
                            <p>Remplacez votre mot de passe temporaire par un mot de passe personnel conforme aux règles ci-dessous.</p>

                            {error ? <div className="rpp-alert error">{error}</div> : null}
                            {success ? <div className="rpp-alert success">{success}</div> : null}

                            <form className="rpp-form" onSubmit={handleSubmit} autoComplete="on">
                                {[
                                    { key: "currentPassword", label: "Mot de passe actuel", autoComplete: "current-password" },
                                    { key: "newPassword", label: "Nouveau mot de passe", autoComplete: "new-password" },
                                    { key: "confirmPassword", label: "Confirmer le nouveau mot de passe", autoComplete: "new-password" },
                                ].map((field) => (
                                    <div className="rpp-input-wrap" key={field.key}>
                                        <span className="rpp-input-icon">
                                            <LockIcon />
                                        </span>
                                        <input
                                            type={show[field.key] ? "text" : "password"}
                                            name={field.key}
                                            autoComplete={field.autoComplete}
                                            placeholder={field.label}
                                            value={form[field.key]}
                                            onChange={handleChange}
                                            disabled={loading}
                                            style={{ paddingRight: "42px" }}
                                        />
                                        <button
                                            type="button"
                                            className="rpp-toggle-pw"
                                            onClick={() => togglePassword(field.key)}
                                            disabled={loading}
                                        >
                                            <EyeIcon open={show[field.key]} />
                                        </button>
                                    </div>
                                ))}

                                <div className="rpp-rules cip-rules">
                                    <div className={passwordHints.length ? "ok" : ""}>• 8 caractères minimum</div>
                                    <div className={passwordHints.upper ? "ok" : ""}>• 1 majuscule</div>
                                    <div className={passwordHints.lower ? "ok" : ""}>• 1 minuscule</div>
                                    <div className={passwordHints.digit ? "ok" : ""}>• 1 chiffre</div>
                                    <div className={passwordHints.special ? "ok" : ""}>• 1 caractère spécial</div>
                                    <div className={passwordHints.different ? "ok" : ""}>• différent du mot de passe temporaire</div>
                                    <div className={passwordHints.confirmed ? "ok" : ""}>• confirmation identique</div>
                                </div>

                                <button type="submit" disabled={!formValid || loading}>
                                    {loading ? "Mise à jour en cours..." : "Modifier mon mot de passe"}
                                </button>
                            </form>
                        </div>
                    </div>
                </main>
            </div>
        </>
    );
};

export default ChangeInitialPasswordPage;
